import { getAnalytics, logEvent as firebaseLogEvent } from 'firebase/analytics';
import { getFirestore, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import moment from 'moment';

/**
 * Logs an error to Firebase Analytics and Firestore
 * @param {string} source - The source/component where the error occurred
 * @param {Error} error - The error object
 * @param {Object} [context={}] - Additional context about the error
 */
export const logError = async (source, error, context = {}) => {
  if (process.env.NODE_ENV === 'development') {
    return;
  }
  try {
    const analytics = getAnalytics();
    const db = getFirestore();
    const auth = getAuth();
    const user = auth.currentUser;

    // Enhanced error context for Firebase permission errors
    // Get current screen location information
    const currentPath = window.location.pathname;
    const currentScreen = currentPath.split('/').pop() || 'unknown';
    
    const enhancedContext = {
      ...context,
      error_type: error.code || 'unknown',
      error_message: error.message,
      error_stack: error.stack,
      user_id: user?.uid || 'anonymous',
      user_email: user?.email || 'anonymous',
      is_authenticated: !!user,
      auth_state: user ? 'authenticated' : 'unauthenticated',
      screen_location: currentScreen,
      full_path: currentPath,
      timestamp: moment().unix()
    };

    // Log to Firebase Analytics
    firebaseLogEvent(analytics, 'error', enhancedContext);

    // Log to Firestore for more detailed analysis
    await addDoc(collection(db, 'error_logs'), {
      source,
      ...enhancedContext,
      timestamp: serverTimestamp()
    });
  } catch (analyticsError) {
    // Fallback to console if analytics fails
    // Enhanced fallback logging if analytics fails
    try {
      const fallbackContext = {
        analytics_error_message: analyticsError.message,
        analytics_error_stack: analyticsError.stack,
        original_error_message: error?.message,
        original_error_stack: error?.stack,
        source,
        context,
        timestamp: moment().unix(),
        location: typeof window !== 'undefined' ? window.location.pathname : undefined
      };
      // Avoid recursion: do not call logError from within its own fallback
      if (process.env.NODE_ENV === 'development') {
        // In dev, avoid noisy console but leave a minimal trace if needed
        // no-op
      } else {
        // As a last resort in production, emit a minimal console.error
        // to surface that analytics logging failed
        // eslint-disable-next-line no-console
        console.error('logError Fallback (analytics failed)', fallbackContext);
      }
    } catch (fallbackError) {
      // Final fallback if even this fails
      // eslint-disable-next-line no-console
      console.error('logError Fallback (secondary failure)', fallbackError);
    }
  }
};

/**
 * Logs an event to Firebase Analytics and optionally to Firestore
 * @param {string} eventName - The name of the event
 * @param {Object} [eventData={}] - Additional data about the event
 * @param {boolean} [logToFirestore=false] - Whether to also log to Firestore
 */
export const logEvent = async (eventName, eventData = {}, logToFirestore = false) => {
  if (process.env.NODE_ENV === 'development') {
    return;
  }
  try {
    const analytics = getAnalytics();
    const auth = getAuth();
    const user = auth.currentUser;

    // Add user context to event data
    const enrichedEventData = {
      user_id: user?.uid || 'anonymous',
      user_email: user?.email || 'anonymous',
      ...eventData
    };

    // Log to Firebase Analytics
    firebaseLogEvent(analytics, eventName, enrichedEventData);

    // Optionally log to Firestore for more detailed analysis
    if (logToFirestore) {
      const db = getFirestore();
      await addDoc(collection(db, 'event_logs'), {
        event_name: eventName,
        ...enrichedEventData,
        timestamp: serverTimestamp()
      });
    }
  } catch (error) {
    // Fallback to console if analytics fails
    // Fallback to logError if analytics fails
    try {
      const fallbackContext = {
        analytics_error_message: error.message,
        analytics_error_stack: error.stack,
        timestamp: moment().unix(),
        location: typeof window !== 'undefined' ? window.location.pathname : undefined
      };
      logError('logEvent Fallback', error, fallbackContext);
    } catch (fallbackError) {
      logError('logEvent Fallback', fallbackError);
    }
  }
};

/**
 * Logs a user action to Firestore for detailed analysis
 * @param {string} action - The action performed
 * @param {Object} [actionData={}] - Additional data about the action
 */
export const logUserAction = async (action, actionData = {}) => {
  if (process.env.NODE_ENV === 'development') {
    return;
  }
  try {
    const db = getFirestore();
    const auth = getAuth();
    const user = auth.currentUser;

    await addDoc(collection(db, 'user_actions'), {
      action,
      user_id: user?.uid || 'anonymous',
      user_email: user?.email || 'anonymous',
      timestamp: serverTimestamp(),
      ...actionData
    });
  } catch (error) {
    try {
      const fallbackContext = {
        user_action_logging_error_message: error.message,
        user_action_logging_error_stack: error.stack,
        timestamp: moment().unix(),
        location: typeof window !== 'undefined' ? window.location.pathname : undefined
      };
      logError('logUserAction Fallback', error, fallbackContext);
    } catch (fallbackError) {
      logError('logUserAction Fallback', fallbackError);
    }
  }
};

/**
 * Logs performance metrics to Firestore
 * @param {string} metricName - The name of the performance metric
 * @param {number} value - The value of the metric
 * @param {Object} [metadata={}] - Additional metadata about the metric
 */
export const logPerformanceMetric = async (metricName, value, metadata = {}) => {
  if (process.env.NODE_ENV === 'development') {
    return;
  }
  try {
    const db = getFirestore();
    const auth = getAuth();
    const user = auth.currentUser;

    await addDoc(collection(db, 'performance_metrics'), {
      metric_name: metricName,
      value,
      user_id: user?.uid || 'anonymous',
      user_email: user?.email || 'anonymous',
      timestamp: serverTimestamp(),
      ...metadata
    });
  } catch (error) {
    try {
      const fallbackContext = {
        performance_metric_logging_error_message: error.message,
        performance_metric_logging_error_stack: error.stack,
        timestamp: moment().unix(),
        location: typeof window !== 'undefined' ? window.location.pathname : undefined
      };
      logError('logPerformanceMetric Fallback', error, fallbackContext);
    } catch (fallbackError) {
      logError('logPerformanceMetric Fallback', fallbackError);
    }
  }
}; 